import useAppToast from '@/hooks/useAppToast';
import { downloadRezeptHistoryCsv } from '@/services/admin/rezept-histories';
import type { RezeptHistoryCsvParams } from '@/types/admin-rezept-history';
import { getApiErrorMessage } from '@/utils';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE, SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

export interface RezeptHistoryCsvExportParams {
  rezept_yearmonth: string;
  service_type?: typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2;
  site_type: string;
  export_mode: 'aggregate' | 'divide';
  clinic_number?: string;
  forced_output?: boolean;
}

/**
 * Parse error message from blob response or axios error.
 */
async function extractBlobErrorMessage(error: unknown): Promise<string | null> {
  const axiosError = error as { response?: { data?: Blob; headers?: Record<string, string> } };
  const data = axiosError?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      // Prefer first validation error (from ValidationException) over generic message
      const firstError = json.errors
        ? Object.values(json.errors as Record<string, string[]>).flat()[0]
        : null;
      return firstError || json.message || null;
    } catch {
      // Not JSON, ignore
    }
  }

  return null;
}

/**
 * Hook for downloading rezept history CSV(s).
 * Backend handles both service types in a single request when service_type is omitted.
 */
export const useRezeptHistoryCsv = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const toast = useAppToast();

  return useMutation<void, Error, RezeptHistoryCsvExportParams>({
    mutationFn: async (params: RezeptHistoryCsvExportParams) => {
      const csvParams: RezeptHistoryCsvParams = {
        rezept_yearmonth: params.rezept_yearmonth,
        service_type: params.service_type || undefined,
        site_type: params.site_type,
        export_mode: params.export_mode,
        clinic_number: params.clinic_number || undefined,
        forced_output: params.forced_output || undefined,
      };

      try {
        await downloadRezeptHistoryCsv(currentSite, csvParams);
        toast.success(CSV_SUCCESS_MESSAGE);
      } catch (error: unknown) {
        const blobMessage = await extractBlobErrorMessage(error);
        if (blobMessage) {
          toast.error(blobMessage);
        } else {
          const message =
            (error instanceof Error ? error.message : null) ||
            getApiErrorMessage(error, CSV_ERROR_MESSAGE);
          toast.error(message);
        }
        throw error;
      }
    },
  });
};
